/**
 * Recovery from webpack lazy-chunk load failures.
 *
 * When a deploy rotates hashed assets (or a transient CDN/network blip occurs),
 * a code-split chunk the app tries to import can 404, throwing
 * "ChunkLoadError: Loading chunk N failed." The lazy route then never mounts. In
 * this app that error is caught by the router's <ErrorBoundary>, which renders a
 * permanent spinner ("infinite loading"). A single hard reload re-fetches
 * index.html and the current chunk hashes, resolving the transient case.
 *
 * Guarding against reload loops is the tricky part: a genuinely-missing chunk
 * would fail again after the reload. We allow up to a small number of reloads
 * within a time window (anchored at the first reload) and refuse further reloads
 * once that cap is hit, so a still-failing chunk surfaces the error instead of
 * looping. The window + count are persisted so they survive the reload (unlike an
 * in-memory flag) -- that is what makes the guard effective across the reload
 * boundary. sessionStorage is the primary store; when it is unavailable (Safari
 * private mode, corporate policy, quota) we fall back to a short-lived cookie,
 * which also survives the reload, so the cap still holds and a stale deploy cannot
 * loop forever. An in-memory copy is the last resort within a single JS context.
 */

import { recordRumEvent, RumEventType } from 'utils/cloudWatchRUM';

/** Key holding "<firstReloadEpochMs>:<count>" for the current window (sessionStorage and cookie fallback). */
export const CHUNK_RELOAD_STATE_KEY = 'policy-chunk-reload-state';

/**
 * Max chunk-error reloads allowed within one window. A genuinely-missing chunk
 * fails again right after each reload, so this caps the loop; a transient blip
 * usually recovers on the first reload.
 */
export const MAX_RELOADS = 2;

/**
 * Length of the reload window, anchored at the first reload. Must comfortably
 * exceed reload + app boot + failing re-import so the cap is enforced across a
 * loop; short enough that a later, unrelated transient failure starts a fresh
 * window and can still recover.
 */
export const RELOAD_WINDOW_MS = 30_000;

/** In-memory fallback state used only when neither sessionStorage nor cookies are available. */
let inMemoryFirstReloadTs = 0;
let inMemoryReloadCount = 0;

/**
 * Set once a reload has been scheduled in this JS context, before
 * window.location.reload() navigates away. Guards against a second chunk error
 * (e.g. a JS chunk and its CSS chunk failing in the same frame) burning another
 * slot of the reload budget before the page unloads.
 */
let reloadScheduled = false;

/** Reset module state between tests. Test-only; not used in production code. */
export const __resetChunkReloadStateForTests = (): void => {
  inMemoryFirstReloadTs = 0;
  inMemoryReloadCount = 0;
  reloadScheduled = false;
};

/** Read the reload state from a cookie (fallback when sessionStorage is blocked). */
const readReloadCookie = (): string | null => {
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${CHUNK_RELOAD_STATE_KEY}=([^;]*)`)
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
};

/**
 * Persist the reload state to a cookie that outlives the reload. Scoped to the
 * window's reload window so it self-expires and cannot suppress a genuinely later
 * failure.
 */
const writeReloadCookie = (value: string): void => {
  try {
    const maxAgeSeconds = Math.ceil(RELOAD_WINDOW_MS / 1000);
    document.cookie = `${CHUNK_RELOAD_STATE_KEY}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}`;
  } catch {
    // Cookies unavailable too; in-memory guard is the last resort.
  }
};

/**
 * True when the given error (Error object or message string) is a webpack
 * chunk-load failure. Checks the error name ('ChunkLoadError' for JS chunks) as
 * well as the message text (CSS chunks and cases where only a message is
 * available). The message pattern intentionally allows any chunk identifier so
 * webpack 5 named chunks (e.g. vendors-node_modules_...) match, not just numeric ids.
 */
export const isChunkLoadError = (input?: unknown): boolean => {
  if (!input) {
    return false;
  }
  const err = input as { name?: unknown; message?: unknown };
  const name = typeof err.name === 'string' ? err.name : '';
  const message =
    typeof err.message === 'string'
      ? err.message
      : typeof input === 'string'
        ? input
        : '';
  return (
    name === 'ChunkLoadError' ||
    message.includes('ChunkLoadError') ||
    // Restrict the chunk identifier to valid webpack chunk names (digits for
    // numbered chunks, plus letters/dots/dashes/underscores for named chunks) so
    // an unrelated message that merely contains "Loading ... chunk ... failed"
    // can't trigger a spurious full-page reload.
    /Loading (?:CSS )?chunk [\w.-]+ failed/i.test(message)
  );
};

const parseState = (raw: string | null): { firstTs: number; count: number } => {
  const [ts, count] = (raw || '').split(':');
  return { firstTs: Number(ts) || 0, count: Number(count) || 0 };
};

/**
 * Read the current window's anchor timestamp + reload count. Prefers
 * sessionStorage, then a cookie (both survive the reload), then the in-memory
 * copy (does not survive reload, so it is a last resort only).
 */
const readReloadState = (): { firstTs: number; count: number } => {
  try {
    const stored = sessionStorage.getItem(CHUNK_RELOAD_STATE_KEY);
    if (stored) {
      return parseState(stored);
    }
  } catch {
    // sessionStorage unavailable; fall through to the cookie.
  }
  const cookie = readReloadCookie();
  if (cookie) {
    return parseState(cookie);
  }
  return { firstTs: inMemoryFirstReloadTs, count: inMemoryReloadCount };
};

/**
 * Persist the window anchor + reload count. Writes sessionStorage AND a cookie so
 * the cap survives the reload even when sessionStorage is blocked (Safari private
 * mode, corporate policy) -- preventing an infinite reload loop on a stale deploy.
 * Also keeps an in-memory copy for same-context reads.
 */
const writeReloadState = (firstTs: number, count: number): void => {
  inMemoryFirstReloadTs = firstTs;
  inMemoryReloadCount = count;
  const value = `${firstTs}:${count}`;
  try {
    sessionStorage.setItem(CHUNK_RELOAD_STATE_KEY, value);
  } catch {
    // sessionStorage unavailable; the cookie below is the reload-surviving guard.
  }
  writeReloadCookie(value);
};

/** Best-effort observability so recovery reloads are visible in CloudWatch RUM. */
const recordChunkReloadEvent = (input?: unknown): void => {
  try {
    const err = input as { message?: unknown };
    const message =
      typeof input === 'string'
        ? input
        : typeof err?.message === 'string'
          ? err.message
          : 'unknown';
    recordRumEvent(RumEventType.ChunkLoadRecovery, {
      message,
      url: window.location.href,
    });
  } catch {
    // Observability is best-effort; never let it block recovery.
  }
};

/**
 * If the given error is a chunk-load failure, trigger a guarded reload to
 * re-fetch current chunk hashes -- up to MAX_RELOADS times within RELOAD_WINDOW_MS
 * (anchored at the first reload), then stop so a genuinely-missing chunk surfaces
 * the error instead of looping. Returns true when a reload was triggered so
 * callers can suppress the default error handling (console noise).
 *
 * @param input the caught error (Error object or message string)
 * @returns true if a reload was triggered, false otherwise
 */
export const recoverFromChunkError = (input?: unknown): boolean => {
  if (!isChunkLoadError(input)) {
    return false;
  }
  // A reload is already scheduled for this page (e.g. a JS chunk and its CSS chunk
  // failed in the same frame). Don't consume another slot of the budget before the
  // page navigates away.
  if (reloadScheduled) {
    return true;
  }
  const now = Date.now();
  const { firstTs, count } = readReloadState();
  const windowActive = firstTs !== 0 && now - firstTs < RELOAD_WINDOW_MS;

  if (windowActive) {
    if (count >= MAX_RELOADS) {
      // Hit the cap within the window and the chunk is still failing -> it is
      // genuinely gone, not a transient blip. Stop, so the error can surface.
      return false;
    }
    writeReloadState(firstTs, count + 1);
  } else {
    // No active window (first failure, or the previous window has elapsed) ->
    // start a fresh window anchored now.
    writeReloadState(now, 1);
  }

  reloadScheduled = true;
  recordChunkReloadEvent(input);
  window.location.reload();
  return true;
};
